import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from 'src/components/ui/checkbox';
import PipelineDataTable, {
  candidateShape,
  columnShape,
} from './PipelineDataTable';
import ManualActionsBar from './ManualActionsBar';
import { SelectionProvider } from './SelectionContext';
import { filterActionsForRows } from '../actionConditions';

// Shared "selectable" table shell — checkbox column + selection state +
// the manual_actions bulk bar + an optional per-row accent border — used by
// any tab that offers row selection (checkboxes appear on more tabs than
// just the ones with a bulk action today, matching the reference layout;
// ManualActionsBar itself simply renders nothing if the current status has
// no matching registered action, so selecting rows on a tab with no bulk
// action is harmless, just inert). Tabs that shouldn't offer selection at
// all (e.g. Non Responsive) skip this and use PipelineDataTable directly.
const SelectableTabTable = ({
  columns,
  candidates,
  isLoading,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  opportunityId,
  assignmentId,
  stateMachineKey,
  manualActions,
  onActionSuccess,
  getRowAccentClassName,
  emptyState,
  onRowClick,
  expandable,
  expandedRowIds,
  expandedRowRender,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);

  const allSelected =
    candidates.length > 0 &&
    candidates.every((c) => selectedIds.includes(c.candidate_id));

  const toggleAll = (checked) => {
    setSelectedIds(checked ? candidates.map((c) => c.candidate_id) : []);
  };

  const toggleOne = (candidateId) => {
    setSelectedIds((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const selectColumn = useMemo(
    () => ({
      key: 'select',
      header: (
        <div className="flex items-center justify-center w-full h-full">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(val) => toggleAll(Boolean(val))}
          />
        </div>
      ),
      className: 'w-11 text-center',
      align: 'center',
      render: (_v, row) => (
        <div
          data-prevent-row-click="true"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
          }}
          role="button"
          tabIndex={0}
          aria-label="Select candidate"
          className="flex items-center justify-center cursor-pointer"
        >
          <Checkbox
            checked={selectedIds.includes(row.candidate_id)}
            onCheckedChange={() => toggleOne(row.candidate_id)}
          />
        </div>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, allSelected]
  );

  const selectedRows = candidates.filter((c) =>
    selectedIds.includes(c.candidate_id)
  );
  const mappingIds = selectedRows.map((c) => c.id).filter(Boolean);
  // selectedIds already *is* the candidate_id list (see toggleOne/toggleAll
  // above) — passed through alongside mappingIds so a bulk action's modal
  // can prefill against the one selected candidate's own profile when
  // exactly one is selected (same single-selection gating the skill-match/
  // interview-schedule prefills already use for mappingIds).
  const candidateIds = selectedIds;

  // A bulk action only belongs in the floating bar when EVERY selected row
  // satisfies its own conditions (actionConditions.js) — firing it against
  // a row that doesn't qualify would just 400 on the backend. With nothing
  // selected this is moot (the bar's own selectedCount === 0 hides it), so
  // fall back to the unfiltered list rather than showing zero actions.
  const bulkManualActions =
    selectedRows.length > 0
      ? filterActionsForRows(manualActions, selectedRows)
      : manualActions;

  const handleActionSuccess = (response) => {
    setSelectedIds([]);
    onActionSuccess(response);
  };

  const rowClassName = (row) => {
    const accent = getRowAccentClassName?.(row);
    return accent ? `border-l-[3px] ${accent}` : undefined;
  };

  return (
    <SelectionProvider value={{ hasSelection: selectedIds.length > 0 }}>
      <div>
        {/* Floating bottom bar, not inline — always mounted (when this tab
            has any resolvable action at all) so its slide-in/out transition
            actually animates instead of the bar just appearing/disappearing
            as selection changes. */}
        <ManualActionsBar
          manualActions={bulkManualActions}
          stateMachineKey={stateMachineKey}
          selectedCount={selectedIds.length}
          mappingIds={mappingIds}
          candidateIds={candidateIds}
          opportunityId={opportunityId}
          assignmentId={assignmentId}
          onSuccess={handleActionSuccess}
          onClear={() => setSelectedIds([])}
        />
        <PipelineDataTable
          columns={[selectColumn, ...columns]}
          candidates={candidates}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          rowClassName={rowClassName}
          emptyState={emptyState}
          onRowClick={onRowClick}
          expandable={expandable}
          expandedRowIds={expandedRowIds}
          expandedRowRender={expandedRowRender}
        />
      </div>
    </SelectionProvider>
  );
};

SelectableTabTable.propTypes = {
  columns: PropTypes.arrayOf(columnShape).isRequired,
  candidates: PropTypes.arrayOf(candidateShape),
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  onActionSuccess: PropTypes.func,
  getRowAccentClassName: PropTypes.func,
  emptyState: PropTypes.node,
  onRowClick: PropTypes.func,
  expandable: PropTypes.bool,
  expandedRowIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  expandedRowRender: PropTypes.func,
};

SelectableTabTable.defaultProps = {
  candidates: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  opportunityId: undefined,
  assignmentId: undefined,
  stateMachineKey: 'default',
  manualActions: [],
  onActionSuccess: () => {},
  getRowAccentClassName: undefined,
  emptyState: null,
  onRowClick: undefined,
  expandable: false,
  expandedRowIds: undefined,
  expandedRowRender: undefined,
};

export default SelectableTabTable;
